import Text from 'components/Text/Text';
import React, { useContext, useEffect } from 'react';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import { Form } from 'antd';
import DeveloperField from './components/DeveloperField/DeveloperField';
import BtnBlock from './components/BtnBlock';
import CategorySelect from './components/CategorySelect';
import ApplicationIdField from './components/ApplicationIdField';
import NameField from './components/NameField';
import VersionCodeField from './components/VersionCodeField';
import VersionNameField from './components/VersionNameField';
import DescriptionField from './components/DescriptionField';
import AppStoreLink from './components/AppStoreLink';
import PlayMarketLink from './components/PlayMarketLink';
import TranslationsMultipleSelect from './components/TranslationsMultipleSelect';
import GameFileBlock from './components/GameFileBlock/GameFileBlock';
import { preventDefaultForEnterKey } from 'utils/helpers';
import {
	CreateGameContext,
	ICreateGameContext,
} from 'modules/Entertainment/Games/CreateGame/createGameState/createGameContext';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);

	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		setIDDeveloper,
		setFetchedDevelopers,
		setIDCategory,
		setFetchedCategories,
		changeApplicationId,
		changeName,
		changeVersionCode,
		changeVersionName,
		changeDescription,
		changeAppStoreLink,
		changePlayMarketLink,
		setIDsLanguages,
		setFetchedLanguages,
		refetchDevelopers,
		changeFileData,
		IDSelectedDeveloper,
		IDSelectedCategory,
		applicationId,
		name,
		versionCode,
		versionName,
		description,
		appStoreLink,
		playMarketLink,
		fileData,
		fileId,
		IDsSelectedLanguages,
		translations,
		needRefetchDevelopers,
	} = useContext<ICreateGameContext>(CreateGameContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({ IDSelectedDeveloper });
		form.setFieldsValue({ IDSelectedCategory });
		form.setFieldsValue({ applicationId });
		form.setFieldsValue({ name });
		form.setFieldsValue({ versionCode });
		form.setFieldsValue({ versionName });
		form.setFieldsValue({ description });
		form.setFieldsValue({ appStoreLink });
		form.setFieldsValue({ playMarketLink });
		form.setFieldsValue({ IDsSelectedLanguages });
	}, [
		IDSelectedDeveloper,
		IDSelectedCategory,
		applicationId,
		name,
		versionCode,
		versionName,
		description,
		appStoreLink,
		playMarketLink,
		IDsSelectedLanguages,
	]);

	const submit = async () => {
		const someTranslationIsEmpty = translations.some(
			(translation) => translation.name === '' || translation.description === '',
		);
		if (someTranslationIsEmpty) {
			changeTypeOfModalWindow('someTranslationIsEmpty');
			return;
		}

		setMainFormValidationFail(false);
		setDataForSubmit({
			developerId: IDSelectedDeveloper || '',
			gameCategoryId: IDSelectedCategory || '',
			applicationId,
			name,
			versionCode: Number(versionCode),
			versionName,
			description,
			gameFileId: fileData.fileId,
			iconFileId: fileId,
			appStoreLink: appStoreLink ? appStoreLink : null,
			playMarketLink: playMarketLink ? playMarketLink : null,
			translations,
		});
		additionalValidationErrors.length
			? setMainFormValidationFail(true)
			: changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFormValidationFail = () => {
		setMainFormValidationFail(true);
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form
				form={form}
				name="basic"
				onFinish={submit}
				onKeyPress={preventDefaultForEnterKey}
				onFinishFailed={handleFormValidationFail}
				layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<DeveloperField
						needRefetchDevelopers={needRefetchDevelopers}
						setIDDeveloper={setIDDeveloper}
						refetchDevelopers={refetchDevelopers}
						setFetchedDevelopers={setFetchedDevelopers}
						changeTypeOfModalWindow={changeTypeOfModalWindow}
					/>
					<CategorySelect setIDCategory={setIDCategory} setFetchedCategories={setFetchedCategories} />
					<ApplicationIdField changeApplicationId={changeApplicationId} />
					<NameField changeName={changeName} />
					<VersionCodeField changeVersionCode={changeVersionCode} />
					<VersionNameField changeVersionName={changeVersionName} />
					<DescriptionField changeDescription={changeDescription} />
					<AppStoreLink changeAppStoreLink={changeAppStoreLink} />
					<PlayMarketLink changePlayMarketLink={changePlayMarketLink} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="setupNewGameFileHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<Text
						className={style.requiredLabel}
						translationNameSpace="form"
						translationKey="requiredLabel"
						theme="secondary"
						size="s"
						weight="bold"
						display="inline"
					/>
					<GameFileBlock
						fileData={fileData}
						changeFileData={changeFileData}
						changeTypeOfModalWindow={changeTypeOfModalWindow}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="translationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<TranslationsMultipleSelect
						setIDsLanguages={setIDsLanguages}
						setFetchedLanguages={setFetchedLanguages}
					/>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
